import {
  BaseMaskCreateAndDeployResponse,
  ClaimedDatabaseResource,
  DataAccessGroupInfo,
  DataGroupAccessAssignmentResponse,
  DataGroupApiAddItemsRequest,
  DataGroupApiCreateDataGroupRequest,
  DataGroupApiDeleteDataGroupRequest,
  DataGroupApiGetClaimedResourcesRequest,
  DataGroupApiGetDataAccessGroupsRequest,
  DataGroupApiGetDataGroupRequest,
  DataGroupApiGetIdentitiesUsingDataRoleRequest,
  DataGroupApiGetJitAccessGroupsRequest,
  DataGroupApiListAccessAssignmentsRequest,
  DataGroupApiListDataGroupsRequest,
  DataGroupApiListPolicyRolesRequest,
  DataGroupApiUpdateAccessAssignmentsRequest,
  DataGroupApiUpdateDataGroupRequest,
  DataGroupItemResponse,
  DataGroupListResponse,
  DataGroupPolicyRoleResponse,
  DataGroupResponse,
  IdentityInfo,
  JITAccessGroupInfo,
  MultiBlockPolicyPreviewResponse,
  MultiBlockPolicyView,
  PageDataGroupListResponse,
  PagePolicyResponse,
  PolicyApiCreateAndDeployBaseMaskPoliciesRequest,
  PolicyApiCreateAndDeployMultiBlockPolicyRequest,
  PolicyApiDeletePolicyRequest,
  PolicyApiGetMultiBlockPolicyRequest,
  PolicyApiGetPolicyRequest,
  PolicyApiListPoliciesRequest,
  PolicyApiPreviewMultiBlockPolicyDdlRequest,
  PolicyApiUpdateAndDeployMultiBlockPolicyRequest,
  PolicyApiUpdatePolicyV2Request,
  PolicyPreviewResponse,
  PolicyResponse,
  ResourceApiGetProviderStructureRequest,
  ResourceApiListSnowflakeTagsRequest,
  RoleDataGroupsResponse,
  TagsResponse,
} from '@ariksa/abac/api';
import {
  CloudAccountApiListAbacAccountsRequest,
  PageCloudAccountResponse,
} from '@ariksa/abac/api';
import {
  CloudAccountApiGetCloudAccountsRequest,
  CloudAccountApiOnboardJitAccountRequest,
  CloudAccountApiReadConfigRequest,
  OnboardingConfigResponse,
  PageCloudAccountGetResponse,
} from '@ariksa/cloud-account/api';
import {
  DataAccessApiGetAuthorizedIdentitiesRequest,
  DataAccessApiGetDataAccessRolesRequest,
  DataAccessIdentityResponse,
  InsightV2ApiInsightV2AllRequest,
  InsightV2ApiInsightV2Request,
  InsightV2Pagination,
} from '@ariksa/inventory-core/api';
import {
  ItemApiListAllRequestsRequest,
  PageAccessRequestGetResponse,
} from '@ariksa/jit';
import {
  AutoApprovalPolicyResponse,
  CloudAccountDeleteResponse,
  ItemApiCreateAutoApprovalPolicyRequest,
  ItemApiDeleteAccountRequest,
  ItemApiDeleteAutoApprovalPolicyRequest,
  ItemApiEditApproversRequest,
  ItemApiGetAutoApprovalPolicyRequest,
  ItemApiGetJitStatusRolesRequest,
  ItemApiListAccountsRequest,
  ItemApiListAutoApprovalPoliciesRequest,
  ItemApiSaveAutoApprovalConfigurationRequest,
  ItemApiUpdateAutoApprovalPolicyRequest,
  ItemApiUpdateJitRolesRequest,
  JitEnabledStatusGetResponse,
  PageCloudAccountInfoGetResponse,
  ResponseUpdateJitRolesV1JitAccountsAccountIdJitRolesPut,
} from '@ariksa/jit/api';
import { PayloadAction } from '@reduxjs/toolkit';
import { QueryAction, QueryStatus } from 'services/types';
import { PagedQueryState } from 'services/utils/PagedQueryState';
import { QueryState } from 'services/utils/QueryState';

import {
  UserServiceListRequest,
  UserServiceListResponse,
} from 'api/auth/api.pb';
import { PageInfo } from 'components/Navigation';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { ContainerState } from './types';

// The initial state of the Settings container
export const initialState: ContainerState = {
  cloudAccounts: QueryState.init([]),
  snowflakeRoles: QueryState.init([]),
  dataGroupsByRole: QueryState.init([]),
  claimedResources: QueryState.init([]),
  snowflakeTags: QueryState.init([]),
  baseMaskPolicyAction: QueryState.init({} as BaseMaskCreateAndDeployResponse),
  policyAction: QueryState.init({}),
  dataPolicies: PagedQueryState.init([]),
  dataPolicy: QueryState.init({} as PolicyResponse),
  multiBlockPolicy: QueryState.init({} as MultiBlockPolicyView),
  structure: QueryState.init({}),
  resources: {},
  tagKeyValues: QueryState.init([]),
  generatePolicy: QueryState.init({} as PolicyPreviewResponse),

  accounts: PagedQueryState.init([]),
  jitAccountList: QueryState.init([]),
  abacAccounts: QueryState.init([]),

  jitAction: QueryState.init({}),
  addJitApprover: QueryState.init({}),
  editJitApprover: QueryState.init({}),
  jitAccounts: PagedQueryState.init([]),
  dataGroups: QueryState.init([]),
  dataAccessGroups: PagedQueryState.init([]),
  dataGroup: QueryState.init({} as DataGroupResponse),
  dataGroupAction: QueryState.init({}),
  dataGroupDrawerItems: QueryState.init([]),
  authorizedGroups: QueryState.init([]),
  entitiesWithAccessToDataGroup: QueryState.init([]),
  identitiesUsingDataRole: QueryState.init([]),
  configure: {
    authorizedIdentities: QueryState.init([]),
    roles: QueryState.init([]),
    users: QueryState.init([]),
    saveConfiguration: QueryState.init({}),
    configuration: QueryState.init([]),
  },
  dataGroupRoles: {},
  approvalPolicies: QueryState.init([]),
  approvalPolicy: QueryState.init({} as AutoApprovalPolicyResponse),
  jitApproverList: QueryState.init([]),
  deleteJitApprover: QueryState.init(null),

  jitApproveRequests: PagedQueryState.init([]),

  onboardingConfig: QueryState.init({} as any),

  roles: QueryState.init({} as any),
  updateJitRoles: QueryState.init({} as any),
};

const jitSetupSlice = createSlice({
  name: 'jitSetupState',
  initialState,
  reducers: {
    getDataAccessCloudAccounts(
      state,
      action: QueryAction<
        PageCloudAccountResponse,
        CloudAccountApiListAbacAccountsRequest
      >,
    ) {
      state.cloudAccounts = QueryState.next(state.cloudAccounts, action, {
        mapData: response => response?.items ?? [],
      });
    },
    getSnowflakeRoles(state, action: QueryAction<any>) {
      state.snowflakeRoles = QueryState.next(state.snowflakeRoles, action, {
        mapData: response => response?.items ?? [],
      });
    },
    getDataGroupsByRole(state, action: QueryAction<RoleDataGroupsResponse[]>) {
      state.dataGroupsByRole = QueryState.next(state.dataGroupsByRole, action);
    },
    getClaimedResources(
      state,
      action: QueryAction<
        ClaimedDatabaseResource[],
        DataGroupApiGetClaimedResourcesRequest
      >,
    ) {
      state.claimedResources = QueryState.next(state.claimedResources, action);
    },
    getResources(state, action: QueryAction<any>) {
      const resourceType = action.payload.q.insightV2RequestBase.resource_type;
      if (!state.resources[resourceType!]) {
        state.resources[resourceType!] = QueryState.init([] as any[], {
          status: QueryStatus.pending,
        });
        return;
      }
      state.resources[resourceType!] = QueryState.next(
        state.resources[resourceType!],
        action,
      );
    },
    getCloudAccounts(
      state,
      action: QueryAction<
        PageCloudAccountGetResponse,
        CloudAccountApiGetCloudAccountsRequest
      >,
    ) {
      state.accounts = PagedQueryState.next(state.accounts, action, {
        mapTotalCount: r => r.total || 0,
        mapData: r => r.items,
      });
    },

    addJitAccount(
      state,
      action: QueryAction<any, CloudAccountApiOnboardJitAccountRequest>,
    ) {
      state.jitAction = QueryState.next(state.jitAction, action);
    },

    addJitApprover(
      state,
      action: QueryAction<string, ItemApiEditApproversRequest>,
    ) {
      state.addJitApprover = QueryState.next(state.addJitApprover, action);
    },
    editJitApprover(
      state,
      action: QueryAction<any, ItemApiEditApproversRequest>,
    ) {
      state.editJitApprover = QueryState.next(state.editJitApprover, action);
    },
    getJitAccounts(
      state,
      action: QueryAction<
        PageCloudAccountInfoGetResponse,
        ItemApiListAccountsRequest
      >,
    ) {
      state.jitAccounts = PagedQueryState.next(state.jitAccounts, action, {
        mapTotalCount: r => r.total || 0,
        mapData: r => r.items,
      });
    },
    getAbacAccounts(
      state,
      action: QueryAction<
        PageCloudAccountResponse,
        CloudAccountApiListAbacAccountsRequest
      >,
    ) {
      state.abacAccounts = QueryState.next(state.abacAccounts, action, {
        mapData: r => r.items ?? [],
      });
    },
    getJitAccountList(
      state,
      action: QueryAction<
        PageCloudAccountInfoGetResponse,
        ItemApiListAccountsRequest
      >,
    ) {
      state.jitAccountList = QueryState.next(state.jitAccountList, action, {
        mapData: r => r.items ?? [],
      });
    },
    getJitApproverList(
      state,
      action: QueryAction<UserServiceListResponse, UserServiceListRequest>,
    ) {
      state.jitApproverList = QueryState.next(state.jitApproverList, action, {
        mapData: r => r.users ?? [],
      });
    },
    deleteJitAccount(
      state,
      action: QueryAction<
        CloudAccountDeleteResponse,
        ItemApiDeleteAccountRequest
      >,
    ) {
      state.deleteJitApprover = QueryState.next(
        state.deleteJitApprover,
        action,
      );
    },

    getJitApproveRequests(
      state,
      action: QueryAction<
        PageAccessRequestGetResponse,
        ItemApiListAllRequestsRequest
      >,
    ) {
      state.jitApproveRequests = PagedQueryState.next(
        state.jitApproveRequests,
        action,
        {
          mapTotalCount: r => r.total || 0,
          mapData: r => r.items,
        },
      );
    },

    getOnboardingConfig(
      state,
      action: QueryAction<
        OnboardingConfigResponse,
        CloudAccountApiReadConfigRequest
      >,
    ) {
      state.onboardingConfig = QueryState.next(state.onboardingConfig, action);
    },
    getRoles(
      state,
      action: QueryAction<
        JitEnabledStatusGetResponse,
        ItemApiGetJitStatusRolesRequest
      >,
    ) {
      state.roles = QueryState.next(state.roles, action);
    },
    updateJitRoles(
      state,
      action: QueryAction<
        ResponseUpdateJitRolesV1JitAccountsAccountIdJitRolesPut,
        ItemApiUpdateJitRolesRequest
      >,
    ) {
      state.updateJitRoles = QueryState.next(state.updateJitRoles, action);
    },

    configureJitApproval(
      state,
      action: QueryAction<any, ItemApiSaveAutoApprovalConfigurationRequest>,
    ) {
      state.jitAction = QueryState.next(state.jitAction, action);
    },
    getDataGroups(
      state,
      action: QueryAction<
        PageDataGroupListResponse,
        DataGroupApiListDataGroupsRequest
      >,
    ) {
      state.dataGroups = QueryState.next(state.dataGroups, action, {
        mapData: r => r.items ?? [],
      });
    },

    getApprovalPolicies(
      state,
      action: QueryAction<
        AutoApprovalPolicyResponse[],
        ItemApiListAutoApprovalPoliciesRequest
      >,
    ) {
      state.approvalPolicies = QueryState.next(state.approvalPolicies, action);
    },
    getApprovalPolicy(
      state,
      action: QueryAction<
        AutoApprovalPolicyResponse,
        ItemApiGetAutoApprovalPolicyRequest
      >,
    ) {
      state.approvalPolicy = QueryState.next(state.approvalPolicy, action);
    },
    createApprovalPolicy(
      state,
      action: QueryAction<any, ItemApiCreateAutoApprovalPolicyRequest>,
    ) {
      state.jitAction = QueryState.next(state.jitAction, action);
    },
    updateApprovalPolicy(
      state,
      action: QueryAction<any, ItemApiUpdateAutoApprovalPolicyRequest>,
    ) {
      state.jitAction = QueryState.next(state.jitAction, action);
    },
    deleteApprovalPolicy(
      state,
      action: QueryAction<any, ItemApiDeleteAutoApprovalPolicyRequest>,
    ) {
      state.jitAction = QueryState.next(state.jitAction, action);
    },

    listSnowflakeTags(
      state,
      action: QueryAction<TagsResponse, ResourceApiListSnowflakeTagsRequest>,
    ) {
      state.snowflakeTags = QueryState.next(state.snowflakeTags, action, {
        mapData: response => response?.tags ?? [],
      });
    },
    createAndDeployBaseMaskPolicies(
      state,
      action: QueryAction<
        BaseMaskCreateAndDeployResponse,
        PolicyApiCreateAndDeployBaseMaskPoliciesRequest
      >,
    ) {
      state.baseMaskPolicyAction = QueryState.next(
        state.baseMaskPolicyAction,
        action,
      );
    },
    getPolicyById(
      state,
      action: QueryAction<PolicyResponse, PolicyApiGetPolicyRequest>,
    ) {
      state.dataPolicy = QueryState.next(state.dataPolicy, action);
    },
    getMultiBlockPolicy(
      state,
      action: QueryAction<
        MultiBlockPolicyView,
        PolicyApiGetMultiBlockPolicyRequest
      >,
    ) {
      state.multiBlockPolicy = QueryState.next(state.multiBlockPolicy, action);
    },
    getDataPolicies(
      state,
      action: QueryAction<PagePolicyResponse, PolicyApiListPoliciesRequest>,
    ) {
      state.dataPolicies = PagedQueryState.next(state.dataPolicies, action, {
        mapTotalCount: r => r.total ?? 0,
        mapData: r => r.items,
      });
    },
    updatePoliciesPagination(state, action: PayloadAction<PageInfo>) {
      state.dataPolicies.page.info = action.payload;
    },
    createPolicy(
      state,
      action: QueryAction<any, PolicyApiCreateAndDeployMultiBlockPolicyRequest>,
    ) {
      state.policyAction = QueryState.next(state.policyAction, action);
    },
    updateAndDeployMultiBlockPolicy(
      state,
      action: QueryAction<any, PolicyApiUpdateAndDeployMultiBlockPolicyRequest>,
    ) {
      state.policyAction = QueryState.next(state.policyAction, action);
    },
    deletePolicy(
      state,
      action: QueryAction<any, PolicyApiDeletePolicyRequest>,
    ) {
      state.policyAction = QueryState.next(state.policyAction, action);
    },
    updatePolicy(
      state,
      action: QueryAction<any, PolicyApiUpdatePolicyV2Request>,
    ) {
      state.policyAction = QueryState.next(state.policyAction, action);
    },
    getStructure(
      state,
      action: QueryAction<
        Record<string, any>,
        ResourceApiGetProviderStructureRequest
      >,
    ) {
      state.structure = QueryState.next(state.structure, action);
    },
    getTagKeyValues(
      state,
      action: QueryAction<TagsResponse, ResourceApiListSnowflakeTagsRequest>,
    ) {
      state.tagKeyValues = QueryState.next(state.tagKeyValues, action, {
        mapData: response => response?.tags ?? [],
      });
    },
    generatePolicy(
      state,
      action: QueryAction<
        MultiBlockPolicyPreviewResponse,
        PolicyApiPreviewMultiBlockPolicyDdlRequest
      >,
    ) {
      state.generatePolicy = QueryState.next(state.generatePolicy, action);
    },
    getIdentitiesUsingDataRole(
      state,
      action: QueryAction<
        IdentityInfo[],
        DataGroupApiGetIdentitiesUsingDataRoleRequest
      >,
    ) {
      state.identitiesUsingDataRole = QueryState.next(
        state.identitiesUsingDataRole,
        action,
      );
    },
    getDataAccessGroups(
      state,
      action: QueryAction<
        PageDataGroupListResponse,
        DataGroupApiListDataGroupsRequest
      >,
    ) {
      state.dataAccessGroups = PagedQueryState.next(
        state.dataAccessGroups,
        action,
        {
          mapData: response => response.items ?? [],
          mapTotalCount: response => response.total ?? 0,
        },
      );
    },
    createDataGroup(
      state,
      action: QueryAction<
        DataGroupResponse,
        DataGroupApiCreateDataGroupRequest
      >,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },
    deleteDataGroup(
      state,
      action: QueryAction<
        DataGroupResponse,
        DataGroupApiDeleteDataGroupRequest
      >,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },
    updateDataGroup(
      state,
      action: QueryAction<
        DataGroupResponse,
        DataGroupApiUpdateDataGroupRequest
      >,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },
    addDataGroupItems(
      state,
      action: QueryAction<DataGroupItemResponse[], DataGroupApiAddItemsRequest>,
    ) {
      state.dataGroupAction = QueryState.next(state.dataGroupAction, action);
    },
    getDataGroup(
      state,
      action: QueryAction<DataGroupResponse, DataGroupApiGetDataGroupRequest>,
    ) {
      state.dataGroup = QueryState.next(state.dataGroup, action);
    },
    getCloudRolesWithMasksForADataGroup(
      state,
      action: QueryAction<
        DataGroupPolicyRoleResponse[],
        DataGroupApiListPolicyRolesRequest
      >,
    ) {
      state.dataGroupDrawerItems = QueryState.next(
        state.dataGroupDrawerItems,
        action,
      );
    },
    getAuthorizedIdentities(
      state,
      action: QueryAction<
        DataAccessIdentityResponse[],
        DataAccessApiGetAuthorizedIdentitiesRequest
      >,
    ) {
      state.configure.authorizedIdentities = QueryState.next(
        state.configure.authorizedIdentities,
        action,
      );
    },
    getDataAccessRolesOptions(
      state,
      action: QueryAction<
        DataAccessIdentityResponse[],
        DataAccessApiGetDataAccessRolesRequest
      >,
    ) {
      state.configure.roles = QueryState.next(state.configure.roles, action);
    },
    getUsers(
      state,
      action: QueryAction<
        DataAccessIdentityResponse[],
        DataAccessApiGetAuthorizedIdentitiesRequest
      >,
    ) {
      state.configure.users = QueryState.next(state.configure.users, action);
    },
    saveConfiguration(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiUpdateAccessAssignmentsRequest
      >,
    ) {
      state.configure.saveConfiguration = QueryState.next(
        state.configure.saveConfiguration,
        action,
      );
    },
    getConfiguration(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiListAccessAssignmentsRequest
      >,
    ) {
      state.configure.configuration = QueryState.next(
        state.configure.configuration,
        action,
      );
    },
    getResourceDetails(
      state,
      action: QueryAction<InsightV2Pagination, InsightV2ApiInsightV2Request>,
    ) {
      state.entitiesWithAccessToDataGroup = QueryState.next(
        state.entitiesWithAccessToDataGroup,
        action,
        { mapData: response => response.items ?? [] },
      );
    },
    getDataAccessRoles(
      state,
      action: QueryAction<
        DataGroupAccessAssignmentResponse[],
        DataGroupApiListAccessAssignmentsRequest
      >,
    ) {
      state.dataGroupDrawerItems = QueryState.next(
        state.dataGroupDrawerItems,
        action,
      );
    },
    getDataGroupRoles(
      state,
      action: QueryAction<
        DataAccessGroupInfo[],
        DataGroupApiGetDataAccessGroupsRequest
      >,
    ) {
      const dataGroupId = action.payload.q.dataGroupId;
      if (!state.dataGroupRoles[dataGroupId]) {
        state.dataGroupRoles[dataGroupId] = QueryState.init(
          [] as DataAccessGroupInfo[],
          {
            status: QueryStatus.pending,
          },
        );
        return;
      }
      state.dataGroupRoles[dataGroupId] = QueryState.next(
        state.dataGroupRoles[dataGroupId],
        action,
      );
    },
    getJitEnabledOktaGroups(
      state,
      action: QueryAction<
        JITAccessGroupInfo[],
        DataGroupApiGetJitAccessGroupsRequest
      >,
    ) {
      state.dataGroupDrawerItems = QueryState.next(
        state.dataGroupDrawerItems,
        action,
      );
    },
    getAuthorizedGroups(
      state,
      action: QueryAction<
        JITAccessGroupInfo[],
        DataGroupApiGetJitAccessGroupsRequest
      >,
    ) {
      state.authorizedGroups = QueryState.next(state.authorizedGroups, action);
    },
  },
});

export const { actions, reducer, name: sliceKey } = jitSetupSlice;
